import { createContext, useContext } from 'react';
import { enUS, type DeepPartial } from './en-US';
import { ptBR, type Dictionary } from './pt-BR';

export type Locale = 'pt-BR' | 'en-US';

export const SUPPORTED_LOCALES: ReadonlyArray<{ readonly code: Locale; readonly label: string }> = [
  { code: 'pt-BR', label: 'Português (Brasil)' },
  { code: 'en-US', label: 'English (US)' },
];

const DICTIONARIES: Record<Locale, Dictionary | DeepPartial<Dictionary>> = {
  'pt-BR': ptBR,
  'en-US': enUS,
};

/**
 * Mescla o dicionário do idioma escolhido sobre o pt-BR.
 *
 * O fallback é chave a chave: uma tradução incompleta nunca deixa buraco na
 * tela. Em ambiente regulado, string faltando é defeito de usabilidade
 * reportável — melhor exibir o português do que `undefined`.
 */
function mergeDictionary(
  base: Dictionary,
  override: Dictionary | DeepPartial<Dictionary>,
): Dictionary {
  const result: Record<string, unknown> = { ...base };
  for (const [key, value] of Object.entries(override)) {
    if (value === undefined) continue;
    const current = result[key];
    if (
      typeof value === 'object' &&
      value !== null &&
      !Array.isArray(value) &&
      typeof current === 'object' &&
      current !== null
    ) {
      result[key] = mergeDictionary(
        current as Dictionary,
        value as DeepPartial<Dictionary>,
      );
      continue;
    }
    result[key] = value;
  }
  return result as Dictionary;
}

const CACHE = new Map<Locale, Dictionary>();

export function getDictionary(locale: Locale): Dictionary {
  const cached = CACHE.get(locale);
  if (cached) return cached;
  const merged = locale === 'pt-BR' ? ptBR : mergeDictionary(ptBR, DICTIONARIES[locale]);
  CACHE.set(locale, merged);
  return merged;
}

export interface I18nContextValue {
  readonly locale: Locale;
  readonly t: Dictionary;
  readonly setLocale: (locale: Locale) => void;
}

export const I18nContext = createContext<I18nContextValue | null>(null);

export function useI18n(): I18nContextValue {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error('useI18n precisa estar dentro de <I18nProvider>.');
  return ctx;
}

/** Atalho: `const t = useT();` devolve só o dicionário. */
export function useT(): Dictionary {
  return useI18n().t;
}

export { ptBR, enUS };
export type { Dictionary };
export {
  ACCESS_EFFECT_LABEL,
  ACCESS_SCOPE_LABEL,
  ACKNOWLEDGEMENT_STATUS_LABEL,
  APPROVAL_DECISION_LABEL,
  APPROVAL_POLICY_DESCRIPTION,
  APPROVAL_POLICY_LABEL,
  AUDIT_ACTION_LABEL,
  DOCUMENT_STATUS_LABEL,
  NOTICE_FREQUENCY_LABEL,
  PERMISSION_FLAG_DESCRIPTION,
  PERMISSION_FLAG_LABEL,
  SYSTEM_ROLE_LABEL,
  VALIDITY_STATE_LABEL,
} from './pt-BR';
